import { useState, useEffect, useCallback } from 'react';
import { api, getExpiryStatus } from '../api';
import ProductModal from '../components/ProductModal';
import { toast } from '../components/Toast';

export default function Products({ triggerAdd, onAddTriggered }) {
  const [products, setProducts] = useState([]);
  const [drawers, setDrawers] = useState([]);
  const [settings, setSettings] = useState({});
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | expired | expiring | drawer:id
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (triggerAdd) {
      setSelectedProduct(null);
      setShowModal(true);
      onAddTriggered?.();
    }
  }, [triggerAdd, onAddTriggered]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (filter === 'expired') params.set('expired', 'true');
    if (filter === 'expiring') params.set('expiring', 'true');
    if (filter.startsWith('drawer:')) params.set('drawer_id', filter.split(':')[1]);
    const [prods, drs, setts] = await Promise.all([
      api.get(`/products?${params}`),
      api.get('/drawers'),
      api.get('/settings'),
    ]);
    setProducts(prods);
    setDrawers(drs);
    setSettings(setts);
    setLoading(false);
  }, [search, filter]);

  useEffect(() => { load(); }, [load]);

  const deleteProduct = async (id, name) => {
    if (!window.confirm(`"${name}" verwijderen?`)) return;
    await api.delete(`/products/${id}`);
    toast('Product verwijderd');
    load();
  };

  const warningDays = parseInt(settings.expiry_warning_days || '7');

  return (
    <>
      {/* ── Sticky search toolbar ── */}
      <div className="topbar">
        <div className="search-bar">
          <span className="search-icon">🔍</span>
          <input
            placeholder="Zoeken op naam, barcode, categorie..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{ background: 'none', border: 'none', color: 'var(--md-on-surface-variant)', cursor: 'pointer', fontSize: 18, lineHeight: 1, flexShrink: 0 }}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="content">
        {/* Filter pills */}
        <div className="filter-row">
          <button className={`filter-pill ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>Alle</button>
          <button className={`filter-pill ${filter === 'expired' ? 'danger' : ''}`} onClick={() => setFilter(filter === 'expired' ? 'all' : 'expired')}>⚠ Verlopen</button>
          <button className={`filter-pill ${filter === 'expiring' ? 'warn' : ''}`} onClick={() => setFilter(filter === 'expiring' ? 'all' : 'expiring')}>⏰ Bijna verlopen</button>
          {drawers.map(d => (
            <button key={d.id} className={`filter-pill ${filter === `drawer:${d.id}` ? 'active' : ''}`} onClick={() => setFilter(filter === `drawer:${d.id}` ? 'all' : `drawer:${d.id}`)}>
              {d.name}
            </button>
          ))}
        </div>

        {/* Products grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><span className="loading-spinner" /></div>
        ) : products.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <p>{search ? 'Geen producten gevonden.' : 'Geen producten. Voeg er een toe!'}</p>
          </div>
        ) : (
          <div className="products-grid">
            {products.map(p => {
              const exp = getExpiryStatus(p.expiry_date, warningDays);
              return (
                <div
                  key={p.id}
                  className={`product-card ${exp?.status === 'danger' ? 'expired' : ''} ${exp?.status === 'warn' ? 'expiring' : ''}`}
                  onClick={() => { setSelectedProduct(p); setShowModal(true); }}
                >
                  <div className="product-name">{p.name}</div>
                  <div className="product-meta">{p.barcode || ''}</div>
                  <div className="product-tags">
                    {p.category && <span className="tag">{p.category}</span>}
                    {exp && <span className={`expiry-badge ${exp.status}`}>{exp.label}</span>}
                    {!p.expiry_date && <span className="tag" style={{ color: 'var(--md-outline)' }}>geen datum</span>}
                  </div>
                  <div className="product-footer">
                    <div>
                      <div className="product-qty">{p.quantity} {p.unit}</div>
                      <div className="product-drawer">{p.drawer_name || 'Geen lade'}{p.position_notes ? ` · ${p.position_notes}` : ''}</div>
                    </div>
                    <div className="product-actions" onClick={e => e.stopPropagation()}>
                      <button className="btn btn-ghost btn-icon btn-sm" onClick={() => { setSelectedProduct(p); setShowModal(true); }} title="Bewerken">✏️</button>
                      <button className="btn btn-danger btn-icon btn-sm" onClick={() => deleteProduct(p.id, p.name)} title="Verwijderen">🗑</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && (
        <ProductModal
          product={selectedProduct}
          drawers={drawers}
          onClose={() => { setShowModal(false); setSelectedProduct(null); }}
          onSaved={() => { setShowModal(false); setSelectedProduct(null); load(); }}
        />
      )}
    </>
  );
}
